import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bot, CheckCircle2, MessageSquareReply, Pencil, ShoppingCart, TriangleAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandChip } from '@/components/scope-chips';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { conversationClient } from '@/lib/gupshup-client';
import { appliedDiscountList } from '@/lib/discounts-client';
import { itemsDiscountExcludingLoyalty, loyaltyRedemptionOfCart } from '@/lib/loyalty-redemption';
import { ApiError } from '@/lib/api-error';
import {
  draftOrderClient,
  type ApprovalReason,
  type CartView,
  type CatalogItem,
  type ConfirmDraftResult,
} from '@/lib/draft-order-client';
import { CartBlockers, describeBlocker } from './cart-blockers';
import { CartCouponSection } from './cart-coupon-section';
import { CartDeliverySection, type CartBranch } from './cart-delivery-section';
import { CartItemsSection } from './cart-items-section';
import { CartPaymentSection } from './cart-payment-section';
import { LoyaltyRedemptionRow } from '@/components/orders/loyalty-redemption-row';
import { HandbackDialog, type HandbackPayload } from './handback-dialog';
import { ItemEditorDialog } from './item-editor-dialog';
import {
  amountBeforeTip,
  buildSavePayload,
  couponChangeNotice,
  customerCartSummary,
  diffAgainstCart,
  lineFromCatalogItem,
  parseAmount,
  projectedTotal,
  tipAmountError,
  workingCopyFromCart,
  type DraftLine,
  type DraftLineOption,
  type DraftWorkingCopy,
} from './cart-draft';

interface CartPanelProps {
  cart: CartView;
  conversationId: string;
  /** Aggregate sessions only: the brand this conversation belongs to. */
  brandName?: string;
  /**
   * Branches this session may place for, already scoped to it. Used only to attribute a cart the
   * agent never located — a pinned branch is not re-pointable from here.
   */
  branches: CartBranch[];
  /** True while the conversation's AI is paused — i.e. the operator already holds the floor. */
  hasTakenOver: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
  onCartChanged: () => void;
  /**
   * Hand the confirmation result up rather than rendering it here.
   *
   * Confirming ends the draft, and this panel only exists while there is one — so anything shown from
   * local state at that moment (the summary with the tracking URL and the payment link) is destroyed
   * by the very refetch that follows. The owner outlives the cart; this component does not.
   */
  onConfirmed: (result: ConfirmDraftResult) => void;
}

/**
 * The operator's live cart.
 *
 * Read-only while the agent is still driving the conversation, because editing then would have the
 * two of them writing over each other. Once the AI is paused — by the button here or by anyone
 * replying from WhatsApp — edits are staged locally and saved as one snapshot; the server re-prices
 * every line from the catalog, so nothing here is trusted as an amount.
 */
export function CartPanel({
  cart,
  conversationId,
  brandName,
  branches,
  hasTakenOver,
  onDirtyChange,
  onClose,
  onCartChanged,
  onConfirmed,
}: CartPanelProps) {
  const { t } = useTranslation('conversations');

  const [copy, setCopy] = useState<DraftWorkingCopy>(() => workingCopyFromCart(cart));
  const [baseline, setBaseline] = useState<CartView>(cart);
  // Server truth is `hasTakenOver`; this only bridges the moment between the takeover call
  // succeeding and the refetched conversation carrying the new pause, so the panel does not flash
  // back to its read-only state.
  const [justTookOver, setJustTookOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editorState, setEditorState] = useState<{ item: CatalogItem | null; line?: DraftLine } | null>(null);
  const [handbackOpen, setHandbackOpen] = useState(false);
  const [handingBack, setHandingBack] = useState(false);
  const [couponBusy, setCouponBusy] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);

  const dirty = useMemo(() => diffAgainstCart(copy, baseline), [copy, baseline]);

  /**
   * Editable exactly while the conversation's AI is paused — however it got paused. An operator who
   * already took over by replying from WhatsApp should not have to press the button again, and the
   * guarantee that matters is only ever "the agent will not write to this cart concurrently".
   */
  const editable = hasTakenOver || justTookOver;

  useEffect(() => {
    onDirtyChange(dirty.any);
  }, [dirty.any, onDirtyChange]);

  // Drop the optimistic bridge as soon as the server agrees the AI is paused — and, just as
  // importantly, once it says the AI is live again. The takeover window lapses after ~30 minutes, and
  // an operator who left the panel open must not still be editing a cart the agent has resumed
  // writing to. Saving would still be safe (the advisory lock and `expectedUpdatedAt` see to that);
  // it is the "the agent will not touch this concurrently" guarantee that would no longer hold.
  useEffect(() => {
    setJustTookOver(false);
  }, [hasTakenOver]);

  // Adopt server changes only while there is nothing local to lose. With unsaved edits the polling
  // is paused upstream, but a save elsewhere can still land — dropping the operator's work to show
  // it would be the worst possible moment to be helpful.
  useEffect(() => {
    if (dirty.any) return;
    setBaseline(cart);
    setCopy(workingCopyFromCart(cart));
  }, [cart, dirty.any]);

  const { data: catalog } = useQuery({
    queryKey: ['draft-catalog', cart.orderId],
    queryFn: () => draftOrderClient.getCatalog(cart.orderId),
    enabled: editable,
    staleTime: 60_000,
  });

  const { data: savedAddresses = [] } = useQuery({
    queryKey: ['draft-saved-addresses', cart.orderId],
    queryFn: () => draftOrderClient.getSavedAddresses(cart.orderId),
    enabled: editable,
    staleTime: 60_000,
  });

  const patch = useCallback((next: Partial<DraftWorkingCopy>) => setCopy((prev) => ({ ...prev, ...next })), []);

  const handleTakeOver = async () => {
    try {
      await conversationClient.conversations.takeover(conversationId);
      setJustTookOver(true);
      onCartChanged();
    } catch {
      toast.error(t('cart.errors.takeoverFailed'));
    }
  };

  const handleDiscard = () => {
    setCopy(workingCopyFromCart(baseline));
  };

  /**
   * Pull the server's state back in after the server refused something, so the panel stops arguing
   * from a copy of the cart that has been overtaken.
   *
   * This is the escape from a genuine dead end. On a 409 the staged edits are still dirty, which pauses
   * polling and makes the reset effect refuse to adopt anything, and the rejected `expectedUpdatedAt`
   * never moves — so every retry fails identically and the only way out is a Discard the toast never
   * mentions. On a 422 the blockers the footer is explaining are read off that same stale baseline.
   *
   * What is kept depends on what would be lost. With staged edits, only the baseline is re-pointed:
   * the unsaved-changes diff then reads as operator-work-vs-new-truth, which is exactly the decision
   * they need to make, and the next Guardar carries the fresh `expectedUpdatedAt`. With nothing staged
   * the working copy is adopted too — re-baselining alone would raise an unsaved-changes banner for
   * changes the operator never made, and pause polling on the strength of it.
   */
  const resyncAfterFailure = async (error: unknown, hadStagedEdits: boolean) => {
    // A network blip is not a stale-state problem; polling recovers from it on its own.
    if (!(error instanceof ApiError)) return;

    try {
      const { cart: fresh } = await draftOrderClient.getForConversation(conversationId);
      // Gone means it is no longer a draft (confirmed by someone else) — nothing to re-baseline, and
      // the refetch below unmounts this panel.
      if (fresh) {
        setBaseline(fresh);
        if (!hadStagedEdits) setCopy(workingCopyFromCart(fresh));
      }
    } catch {
      // Leave the stale baseline in place — the operator's work is worth more than a tidy banner, and
      // the next poll (or their Discard) picks the new state up anyway.
    }
    onCartChanged();
  };

  const handleSave = async (): Promise<CartView | null> => {
    if (!dirty.saveable) return baseline;

    setSaving(true);
    try {
      const { cart: saved, couponChanges } = await draftOrderClient.save(
        cart.orderId,
        buildSavePayload(copy, baseline, dirty),
      );
      setBaseline(saved);
      setCopy(workingCopyFromCart(saved));
      // Keep the takeover window from lapsing while the operator is clearly still working.
      void conversationClient.conversations.takeover(conversationId).catch(() => undefined);
      onCartChanged();
      toast.success(t('cart.toasts.saved'));
      // A branch move re-checks every typed coupon; the saved cart already shows the outcome, these
      // say why the coupon line changed. One toast per coupon.
      for (const change of couponChanges ?? []) {
        const notice = couponChangeNotice(change);
        toast[notice.tone](t(notice.key, { code: change.code }));
      }
      return saved;
    } catch (error) {
      toast.error(describeError(error, t, baseline.currencySymbol));
      await resyncAfterFailure(error, dirty.any);
      return null;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Confirming never saves on the way past. Guardar is a separate, deliberate step — the same shape
   * the agent works in, where mutating tools and `confirm_order` are distinct calls.
   *
   * That split is what makes the approval advice reachable: an implicit save meant `needsApproval`
   * (which requires a clean baseline) was still false at the moment we confirmed, so an operator who
   * changed the total and pressed Confirmar was never shown the "ask the customer first" advice and
   * the order was recorded as if there had been nothing to approve.
   */
  const handleConfirm = async (withoutCustomerApproval: boolean) => {
    setConfirming(true);
    try {
      const result = await draftOrderClient.confirm(cart.orderId, { withoutCustomerApproval });
      // Hand it up BEFORE the refetch that unmounts this panel.
      onConfirmed(result);
      onCartChanged();
    } catch (error) {
      // A refused confirmation means the cart is not what the panel thinks it is — the blockers the
      // footer explains are read off the baseline, so re-pointing it is what makes the refusal legible.
      // Confirmar is only reachable with nothing staged, hence `false`.
      await resyncAfterFailure(error, false);

      if (error instanceof ApiError && error.code === 'DELIVERY_FEE_CHANGED') {
        // Not a dead end: the server already persisted the new fee (that is what the resync above
        // just showed), so confirming again places the order at the total now on screen. The action
        // keeps that path one click long instead of leaving the operator to rediscover Confirmar —
        // and it goes out flagged as unapproved, because the customer has not seen the new fee.
        toast.warning(describeError(error, t, baseline.currencySymbol), {
          action: { label: t('cart.actions.confirmAnyway'), onClick: () => void handleConfirm(true) },
        });
      } else {
        toast.error(describeError(error, t, baseline.currencySymbol));
      }
    } finally {
      setConfirming(false);
    }
  };

  /**
   * Coupons act on the SERVER cart immediately, unlike everything else in the panel — a coupon is
   * not part of the staged snapshot (the save payload has no field for it), and the backend
   * use-case can extend the cart on its own (an ITEM_DISCOUNT's reward lines). That is exactly why
   * the section is locked while edits are staged: applied under a dirty copy, the following save
   * would silently strip the reward lines the coupon just added. The section runs only against a
   * clean baseline, so adopting the returned cart wholesale loses nothing.
   */
  const handleApplyCoupon = async (code: string): Promise<boolean> => {
    setCouponBusy(true);
    try {
      const outcome = await draftOrderClient.applyCoupon(cart.orderId, code);
      setBaseline(outcome.cart);
      setCopy(workingCopyFromCart(outcome.cart));
      // Keep the takeover window from lapsing while the operator is clearly still working.
      void conversationClient.conversations.takeover(conversationId).catch(() => undefined);
      onCartChanged();
      // A 200 does not mean the code stuck: the kernel may have dropped it (or something else) to
      // keep the cart coherent, and saying "aplicado" over a dropped code is an outright falsehood.
      const typed = code.trim().toUpperCase();
      const conflict = outcome.stackingConflict;
      const labels = (entries: { label: string }[]) => entries.map((entry) => entry.label).join(', ');
      if (conflict?.dropped.some((entry) => entry.code?.toUpperCase() === typed)) {
        toast.warning(
          t('cart.coupon.notStackable', {
            code: typed,
            kept: labels(conflict.kept),
            amount: fmt(outcome.cart.currencySymbol, conflict.totalKept),
          }),
        );
      } else if (conflict?.dropped.length) {
        toast.info(t('cart.coupon.displaced', { code: typed, dropped: labels(conflict.dropped) }));
      } else {
        toast.success(t('cart.coupon.applied', { code: typed }));
      }
      // Rewards the coupon promises but could not add on its own (sold out, or options to choose) —
      // said out loud, or "free fries" quietly becomes a coupon that does nothing.
      if (outcome.pendingRewards?.length) {
        const items = outcome.pendingRewards.map((reward) => reward.name ?? reward.catalogItemId).join(', ');
        toast.warning(t('cart.coupon.pendingRewards', { items }));
      }
      return true;
    } catch (error) {
      toast.error(describeError(error, t, baseline.currencySymbol));
      await resyncAfterFailure(error, false);
      return false;
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = async (code?: string) => {
    setCouponBusy(true);
    try {
      const fresh = await draftOrderClient.removeCoupon(cart.orderId, code);
      setBaseline(fresh);
      setCopy(workingCopyFromCart(fresh));
      void conversationClient.conversations.takeover(conversationId).catch(() => undefined);
      onCartChanged();
      toast.success(t('cart.coupon.removed'));
    } catch (error) {
      toast.error(describeError(error, t, baseline.currencySymbol));
      await resyncAfterFailure(error, false);
    } finally {
      setCouponBusy(false);
    }
  };

  /**
   * The operator vouching for a bank transfer nobody can show a receipt for.
   *
   * Server-side and immediate, like the coupon section and for the same reason: it is not part of
   * the staged snapshot (the save payload has no field for it), and it changes the cart's blockers.
   * Adopting the returned cart wholesale is safe because the action is only offered against a saved
   * `bank_transfer` preference — see `CartPaymentSection`.
   */
  const runTransferOverride = async (
    apply: () => Promise<CartView>,
    successKey: 'cart.toasts.transferConfirmed' | 'cart.toasts.transferReverted',
  ) => {
    setTransferBusy(true);
    try {
      const fresh = await apply();
      setBaseline(fresh);
      if (!dirty.any) setCopy(workingCopyFromCart(fresh));
      // Keep the takeover window from lapsing while the operator is clearly still working.
      void conversationClient.conversations.takeover(conversationId).catch(() => undefined);
      onCartChanged();
      toast.success(t(successKey));
    } catch (error) {
      toast.error(describeError(error, t, baseline.currencySymbol));
      await resyncAfterFailure(error, dirty.any);
    } finally {
      setTransferBusy(false);
    }
  };

  const handleHandback = async ({ message, buttons, requestAgentTurn }: HandbackPayload) => {
    setHandingBack(true);
    try {
      const result = await conversationClient.conversations.resumeAi(conversationId, {
        message,
        buttons,
        requestAgentTurn,
      });
      setHandbackOpen(false);
      setJustTookOver(false);
      onCartChanged();
      // Two different promises to the operator: the customer has been written to, or the agent is
      // about to write. Claiming the second when the turn was not queued would leave them waiting on
      // a message that is never coming.
      toast.success(result.agentTurnQueued ? t('cart.toasts.handedBackToAgent') : t('cart.toasts.handedBack'));
    } catch {
      toast.error(t('cart.errors.handbackFailed'));
    } finally {
      setHandingBack(false);
    }
  };

  const applyEditorSelection = (selection: { quantity: number; options: DraftLineOption[]; comments: string }) => {
    if (!editorState) return;
    const { item, line } = editorState;

    if (line) {
      setCopy((prev) => ({
        ...prev,
        lines: prev.lines.map((candidate) => (candidate.key === line.key ? { ...candidate, ...selection } : candidate)),
      }));
    } else if (item) {
      setCopy((prev) => ({ ...prev, lines: [...prev.lines, lineFromCatalogItem(item, selection)] }));
    }
    setEditorState(null);
  };

  const openEditorForLine = (line: DraftLine) => {
    const item = findCatalogItem(catalog?.categories, line.catalogItemId);
    setEditorState({ item: item ?? null, line });
  };

  const blockers = baseline.blockers ?? [];
  const total = projectedTotal(copy, baseline);
  // Checked here so the operator is told before saving; the server refuses the same tip on save.
  const tipError = tipAmountError(copy, baseline);
  const deliveryFee = parseAmount(copy.deliveryFeeOverride) ?? baseline.deliveryFee;
  // The fee discount's share taken the way `projectedTotal` takes it — struck through beside the
  // net fee, so the rows keep adding up to the total.
  const deliveryFeeDiscount = Math.min(baseline.deliveryFeeDiscountAmount ?? 0, deliveryFee ?? 0);
  // Normalised once: a cart read during the deploy window still answers the singular shape.
  const appliedDiscounts = appliedDiscountList(baseline);
  // Loyalty money on the cart — inside `discountAmount`, never in `appliedDiscounts`: its own row.
  const loyaltyRedemption = loyaltyRedemptionOfCart(baseline);

  /**
   * Confirmar is available exactly when nothing the OPERATOR did is unsaved and nothing is blocking.
   * A cart the agent built and the operator only reviewed is confirmable straight away — no save step
   * is forced on them; it is staged edits that have to be resolved first.
   *
   * Price drift deliberately does NOT block: the order stands at the price the customer agreed to,
   * and the server confirms it at that price. Forcing a save first both moved the total to a number
   * the customer never approved and — when another line had gone sold-out or orphaned in the
   * meantime — turned an all-or-nothing save the operator could never get past into a cart that
   * could not be confirmed at all. Saving stays available as the way to adopt the new prices.
   */
  const confirmable = blockers.length === 0 && !dirty.any;
  /** Nothing stands between this cart and a real order — the customer's go-ahead aside. */
  const placeable = confirmable;
  /**
   * The operator changed something the customer agreed to (or is about to have a saved card charged
   * on their behalf) and hasn't asked them about it. That reorders the footer — it never disables
   * anything. An operator with the customer on the phone knows more than the policy does.
   *
   * Only meaningful against a saved baseline, which is now guaranteed at the point it matters:
   * Confirmar is unreachable while anything is staged, so by the time it can be pressed this verdict
   * is the one the server returned for the state actually on the order. (Price drift does not stale
   * it — the verdict is about operator edits, and nobody staged a repricing.)
   */
  const needsApproval = Boolean(baseline.pendingCustomerApproval) && !dirty.any;

  return (
    <div className="absolute inset-0 z-20 flex h-full w-full flex-col overflow-hidden bg-card md:static md:z-auto md:w-[26rem] md:shrink-0 md:border-l md:border-border">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">
            {t('cart.title', { id: cart.orderId.slice(0, 8) })}
          </span>
          <BrandChip brandName={brandName} />
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label={t('cart.close')}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {dirty.any ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">{t('cart.unsaved')}</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleDiscard} disabled={saving}>
            {t('cart.actions.discard')}
          </Button>
        </div>
      ) : (
        // Nobody staged this — the menu moved underneath the cart. There is nothing to discard, so
        // the only way forward is a save, which re-prices every line from the catalog.
        dirty.priceDrift && (
          <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">{t('cart.priceDrift')}</span>
          </div>
        )
      )}

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        <CartItemsSection
          lines={copy.lines}
          catalog={catalog}
          currencySymbol={baseline.currencySymbol}
          editable={editable}
          onAdd={(item) => setEditorState({ item })}
          onEdit={openEditorForLine}
          onRemove={(key) => setCopy((prev) => ({ ...prev, lines: prev.lines.filter((l) => l.key !== key) }))}
          onQuantityChange={(key, quantity) =>
            setCopy((prev) => ({
              ...prev,
              lines: prev.lines.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, quantity) } : l)),
            }))
          }
        />

        <CartDeliverySection
          copy={copy}
          cart={baseline}
          savedAddresses={savedAddresses}
          branches={branches}
          editable={editable}
          onChange={patch}
        />

        <CartPaymentSection
          copy={copy}
          cart={baseline}
          editable={editable}
          transferBusy={transferBusy}
          onChange={patch}
          onConfirmTransfer={(reason) =>
            void runTransferOverride(
              () => draftOrderClient.markTransferVerified(cart.orderId, reason || undefined),
              'cart.toasts.transferConfirmed',
            )
          }
          onRevertTransfer={() =>
            void runTransferOverride(
              () => draftOrderClient.clearTransferVerification(cart.orderId),
              'cart.toasts.transferReverted',
            )
          }
        />

        <CartCouponSection
          cart={baseline}
          editable={editable}
          locked={dirty.any}
          busy={couponBusy}
          onApply={handleApplyCoupon}
          onRemove={(code) => void handleRemoveCoupon(code)}
        />

        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('cart.extras.title')}
          </h3>
          <div className="space-y-1.5">
            <Label htmlFor="cart-notes" className="text-sm font-medium">
              {t('cart.extras.notes')}
            </Label>
            <Textarea
              id="cart-notes"
              rows={2}
              value={copy.notes}
              disabled={!editable}
              onChange={(event) => patch({ notes: event.target.value })}
              placeholder={t('cart.extras.notesPlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cart-tip" className="text-sm font-medium">
              {t('cart.extras.tip')}
            </Label>
            <Input
              id="cart-tip"
              inputMode="decimal"
              value={copy.tipAmount}
              disabled={!editable}
              aria-invalid={tipError ? true : undefined}
              aria-describedby={tipError ? 'cart-tip-error' : undefined}
              onChange={(event) => patch({ tipAmount: event.target.value })}
              placeholder="0.00"
            />
            {tipError && (
              <p id="cart-tip-error" className="text-xs font-medium text-destructive" role="alert">
                {tipError === 'invalid'
                  ? t('cart.extras.tipInvalid')
                  : t('cart.extras.tipAboveMax', {
                      max: fmt(baseline.currencySymbol, Math.max(0, amountBeforeTip(copy, baseline))),
                    })}
              </p>
            )}
          </div>
        </section>

        <section className="space-y-1 border-t border-border pt-3 text-sm">
          <Row label={t('cart.totals.items')} value={fmt(baseline.currencySymbol, sumLines(copy))} />
          {copy.deliveryType !== 'pickup' && (
            <Row
              label={t('cart.totals.delivery')}
              value={
                deliveryFee == null ? (
                  t('cart.totals.pending')
                ) : deliveryFeeDiscount > 0 ? (
                  <>
                    <s className="mr-1.5 text-muted-foreground/70">{fmt(baseline.currencySymbol, deliveryFee)}</s>
                    {deliveryFee > deliveryFeeDiscount
                      ? fmt(baseline.currencySymbol, deliveryFee - deliveryFeeDiscount)
                      : t('cart.totals.freeDelivery')}
                  </>
                ) : (
                  fmt(baseline.currencySymbol, deliveryFee)
                )
              }
            />
          )}
          {/* One row per applied discount, including at 0 — an applied coupon taking nothing off
              (its reward item is not in the cart) is precisely what the operator has to be able to
              see, and hiding the row would read as "no coupon". */}
          {appliedDiscounts.map((discount, index) => {
            // The cast is the one place that admits the pre-multi-discount shape still reaching
            // this row: §C4 types `discountAmount` as required, but the singular snapshot had none.
            // With exactly one discount the cart's sum (less the loyalty row's share) IS that
            // discount's amount, so it stands in.
            const own = discount.discountAmount as number | undefined;
            const feeShare = discount.deliveryFeeDiscountAmount ?? 0;
            // Items money only: the fee share is already struck out of the delivery row above.
            const amount =
              own !== undefined
                ? own - feeShare
                : appliedDiscounts.length === 1
                  ? itemsDiscountExcludingLoyalty(baseline, loyaltyRedemption)
                  : 0;
            return (
              <Row
                key={discount.discountId || `${discount.code ?? discount.name ?? ''}-${index}`}
                label={`${t('cart.totals.discount')} (${discount.code ?? discount.name ?? ''})`}
                value={
                  amount > 0
                    ? `-${fmt(baseline.currencySymbol, amount)}`
                    : feeShare > 0
                      ? t('cart.totals.discountOnDelivery')
                      : t('cart.totals.discountNoEffect')
                }
              />
            );
          })}
          {loyaltyRedemption && (
            <LoyaltyRedemptionRow
              redemption={loyaltyRedemption}
              variant="compact"
              formatMoney={(amount) => fmt(baseline.currencySymbol, amount)}
            />
          )}
          {(parseAmount(copy.tipAmount) ?? 0) > 0 && (
            <Row
              label={t('cart.totals.tip')}
              value={fmt(baseline.currencySymbol, parseAmount(copy.tipAmount) as number)}
            />
          )}
          <Row label={t('cart.totals.total')} value={fmt(baseline.currencySymbol, total)} strong />
        </section>

        {!dirty.any && <CartBlockers blockers={blockers} currencySymbol={baseline.currencySymbol} />}
      </div>

      <div className="shrink-0 space-y-2 border-t border-border p-4">
        {!editable ? (
          <Button className="w-full" onClick={handleTakeOver}>
            <Pencil className="h-3.5 w-3.5" />
            {t('cart.actions.takeOver')}
          </Button>
        ) : (
          <>
            {needsApproval && <ApprovalNotice reasons={baseline.approvalReasons ?? []} />}

            <Button
              className="w-full"
              loading={saving}
              disabled={!dirty.saveable || tipError !== null}
              onClick={() => void handleSave()}
            >
              {t('cart.actions.save')}
            </Button>

            {/*
              The two exits, ordered by consequence. When the operator changed what the customer
              agreed to, asking them is the primary action and placing it anyway is the deliberate
              second choice — the reverse of the usual arrangement, and the whole point of the
              approval policy.
            */}
            <div className="flex gap-2">
              <Button
                variant={needsApproval ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setHandbackOpen(true)}
                disabled={saving}
              >
                <Bot className="h-3.5 w-3.5" />
                {needsApproval ? t('cart.actions.askCustomer') : t('cart.actions.handBack')}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                loading={confirming}
                disabled={!confirmable}
                title={
                  dirty.any
                    ? t('cart.actions.saveFirst')
                    : blockers.length > 0
                      ? t('cart.blockers.title')
                      : needsApproval
                        ? t('cart.approval.confirmAnywayHint')
                        : undefined
                }
                onClick={() => void handleConfirm(needsApproval)}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {needsApproval ? t('cart.actions.confirmAnyway') : t('cart.actions.confirm')}
              </Button>
            </div>
          </>
        )}
      </div>

      <ItemEditorDialog
        open={editorState !== null}
        item={editorState?.item ?? null}
        line={editorState?.line}
        currencySymbol={baseline.currencySymbol}
        onCancel={() => setEditorState(null)}
        onSubmit={applyEditorSelection}
      />

      <HandbackDialog
        open={handbackOpen}
        suggestion={needsApproval ? t('cart.handback.approvalSuggestion') : t('cart.handback.suggestion')}
        // The saved cart's own summary — the same text the agent would present, so the customer sees
        // one rendering of the order no matter who sent it, with the delivery fee and tip itemized
        // above the fee-inclusive total. Staged edits are not in it yet.
        cartSummary={
          baseline.items.length > 0
            ? customerCartSummary(baseline, {
                deliveryFee: t('cart.totals.delivery'),
                tip: t('cart.totals.tip'),
                freeDelivery: t('cart.totals.freeDelivery'),
              })
            : undefined
        }
        summaryStale={dirty.saveable}
        offerConfirmButton={placeable}
        submitting={handingBack}
        onCancel={() => setHandbackOpen(false)}
        onSubmit={(payload) => void handleHandback(payload)}
      />
    </div>
  );
}

/**
 * Why the panel is suggesting the customer be asked. Naming the reason is what makes the
 * recommendation act like advice instead of an unexplained reordering of the buttons.
 */
function ApprovalNotice({ reasons }: { reasons: ApprovalReason[] }) {
  const { t } = useTranslation('conversations');

  return (
    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-300">
      <MessageSquareReply className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-0.5">
        <p className="font-medium">{t('cart.approval.title')}</p>
        <p>
          {reasons.length > 0
            ? reasons.map((reason) => t(`cart.approval.reasons.${reason}` as const)).join(' · ')
            : t('cart.approval.generic')}
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

const fmt = (symbol: string, amount: number) => `${symbol}${amount.toFixed(2)}`;

function sumLines(copy: DraftWorkingCopy): number {
  return copy.lines.reduce(
    (sum, line) => sum + (line.unitPrice + line.options.reduce((d, o) => d + o.priceDelta, 0)) * line.quantity,
    0,
  );
}

function findCatalogItem(
  categories: { items: CatalogItem[] }[] | undefined,
  catalogItemId: string,
): CatalogItem | undefined {
  for (const category of categories ?? []) {
    const found = category.items.find((item) => item.id === catalogItemId);
    if (found) return found;
  }
  return undefined;
}

/**
 * The sentence for a refusal, from the most specific copy we have.
 *
 * A refused *confirmation* answers with the first blocker's own code (`BELOW_MINIMUM`,
 * `LOCATION_CLOSED`, …), which `cart.errors` does not enumerate — but the blocker checklist already
 * spells every one of them out, numbers and branch names included. Delegating to it is the difference
 * between naming the problem and saying "something went wrong" about a cart that says why on screen.
 */
function describeError(
  error: unknown,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
  currencySymbol: string,
): string {
  if (!(error instanceof ApiError)) return t('cart.errors.generic');

  const generic = t('cart.errors.generic');
  // The refusal carries its bound (C3): the largest tip this order can take right now.
  if (error.code === 'TIP_NOT_ALLOWED') {
    const details = error.data.details as { maxAmount?: unknown } | undefined;
    return t('cart.errors.TIP_NOT_ALLOWED', { max: fmt(currencySymbol, Number(details?.maxAmount ?? 0)) });
  }
  const specific = t(`cart.errors.${error.code}` as const, { defaultValue: '' });
  if (specific) return specific;

  return describeBlocker({ code: error.code, data: error.data }, currencySymbol, t, generic);
}
